import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";

// FIREBASE IMPORTS
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";

// Standardization & Dictionary Utility
import {
  STANDARD_CUISINES,
  STANDARD_MEASUREMENTS,
  SEED_INGREDIENTS,
  toTitleCase,
  getStandardizedUnit,
  parseFraction,
  parseBulkIngredients,
  findDuplicateIngredients
} from "../utils/recipeStandardization";

// Dnd-kit imports
import { DndContext, closestCenter } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// MUI Components & Icons
import { 
  Container, Paper, Typography, Grid, TextField, Button, Box, Select, MenuItem, InputLabel,
  FormControl, IconButton, CircularProgress, Alert, Autocomplete, Chip, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';

const SortableStep = ({ step, index, handleStepChange, deleteStep, error }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: step.id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <Box ref={setNodeRef} style={style} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
      <Box sx={{ pt: 1.5, cursor: 'grab' }} {...attributes} {...listeners}>
        <DragIndicatorIcon color="action" />
      </Box>
      <Typography sx={{ pt: 2, minWidth: 24, fontWeight: 'bold' }}>{index + 1}.</Typography>
      <TextField
        label={`Step ${index + 1}`}
        value={step.instruction}
        onChange={(e) => handleStepChange(index, e.target.value)}
        fullWidth
        multiline
        error={!!error}
        helperText={error}
        placeholder="e.g. Preheat oven to 375°F and line baking sheets with parchment paper."
      />
      <IconButton onClick={() => deleteStep(index)} color="error" sx={{ mt: 1 }}>
        <DeleteIcon />
      </IconButton>
    </Box>
  );
};

const EditRecipe = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = getAuth();
  const fileInputRef = useRef(null);

  const [recipeData, setRecipeData] = useState(null);
  const [cuisinesList, setCuisinesList] = useState(STANDARD_CUISINES);
  const [ingredientsList, setIngredientsList] = useState(SEED_INGREDIENTS);

  // Quick Paste Dialog State
  const [quickPasteOpen, setQuickPasteOpen] = useState(false);
  const [quickPasteText, setQuickPasteText] = useState("");
  const [quickPasteMode, setQuickPasteMode] = useState("replace"); // 'replace' or 'append'

  // Image upload state
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [manualImageUrl, setManualImageUrl] = useState("");

  const [existingTags] = useState([
    "Dinner", "Lunch", "Breakfast", "Dessert", "Vegetarian", "Vegan",
    "Quick", "Healthy", "Comfort Food", "Holiday", "Slow Cooker", "Baking"
  ]);
  
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // --- 1. Fetch Initial Data via Firestore & Central Dictionary ---
  useEffect(() => {
    const fetchDictionary = async () => {
      try {
        const dictDoc = await getDoc(doc(db, "metadata", "dictionary"));
        if (dictDoc.exists()) {
          const data = dictDoc.data();
          if (data.cuisines && Array.isArray(data.cuisines) && data.cuisines.length > 0) {
            setCuisinesList(data.cuisines);
          }
          if (data.ingredients && Array.isArray(data.ingredients) && data.ingredients.length > 0) {
            setIngredientsList(data.ingredients);
          }
        }
      } catch (err) {
        console.log("Using local default dictionary fallback");
      }
    };
    fetchDictionary();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) { navigate('/login'); return; }

      try {
        // Check if user is an admin
        const userDocSnap = await getDoc(doc(db, "users", user.uid));
        const isAdmin = userDocSnap.exists() && userDocSnap.data().role === "admin";

        const docRef = doc(db, "recipes", id);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
          setError("Recipe not found.");
          setIsLoading(false);
          return;
        }

        const data = docSnap.data();

        // Security Check: Only owner or admin can edit
        if (data.user_id !== user.uid && !isAdmin) {
          setError("You do not have permission to edit this recipe.");
          setIsLoading(false);
          return;
        }

        setRecipeData({
          recipe_name: data.recipe_name || data.title || "",
          description: data.description || "",
          cuisine: data.cuisine ? toTitleCase(data.cuisine) : "American",
          prep_time: data.prep_time !== undefined ? data.prep_time : (data.prepTime || ""),
          cook_time: data.cook_time !== undefined ? data.cook_time : (data.cookTime || ""),
          servings: data.servings ? String(data.servings) : "4",
          difficulty: data.difficulty || "Medium",
          
          ingredients: (data.ingredients && data.ingredients.length > 0)
            ? data.ingredients.map(ing => ({
                ingredient_name: ing.ingredient_name || "",
                amount: ing.amount !== undefined ? String(ing.amount) : "",
                measurement_name: ing.measurement_name || ing.measurement_unit || "",
              }))
            : [{ ingredient_name: "", amount: "", measurement_name: "" }],
          
          steps: (data.steps && data.steps.length > 0)
            ? data.steps.map((step, idx) => ({
                id: step.id || Date.now() + idx + Math.random(),
                step_number: step.step_number || idx + 1,
                instruction: typeof step === 'string' ? step : (step.instruction || ""),
              }))
            : [{ id: Date.now(), step_number: 1, instruction: "" }],
          
          images: Array.isArray(data.images) ? data.images.filter(Boolean) : (data.image ? [data.image] : []),
          tags: data.tags || [],
        });

      } catch (err) {
        console.error(err);
        setError("Failed to load recipe data. It may not exist or you don't have permission.");
      } finally {
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, [id, auth, navigate]);

  // --- 2. Unsaved Changes Warning ---
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => { window.removeEventListener('beforeunload', handleBeforeUnload); };
  }, [isDirty]);

  // --- 3. Duplicate Ingredients Detection ---
  const duplicateIngredients = useMemo(() => {
    if (!recipeData || !recipeData.ingredients) return [];
    return findDuplicateIngredients(recipeData.ingredients);
  }, [recipeData]);

  // --- 4. Input Handlers ---
  const handleChange = (e) => {
    setIsDirty(true);
    setRecipeData({ ...recipeData, [e.target.name]: e.target.value });
    if (validationErrors[e.target.name]) {
      setValidationErrors({ ...validationErrors, [e.target.name]: null });
    }
  };

  const handleCuisineChange = (event, newValue) => {
    setIsDirty(true);
    const clean = newValue ? toTitleCase(newValue) : "";
    setRecipeData({ ...recipeData, cuisine: clean });
    if (validationErrors.cuisine) {
      setValidationErrors({ ...validationErrors, cuisine: null });
    }
  };
  
  const handleTagsChange = (event, newValue) => {
    setIsDirty(true);
    setRecipeData({ ...recipeData, tags: newValue });
  };

  const handleIngredientChange = (index, field, value) => {
    setIsDirty(true);
    const updatedIngredients = [...recipeData.ingredients];
    updatedIngredients[index][field] = value;
    setRecipeData({ ...recipeData, ingredients: updatedIngredients });
  };

  const handleIngredientNameBlur = (index, value) => {
    const cleaned = toTitleCase(value);
    if (cleaned !== value) {
      handleIngredientChange(index, "ingredient_name", cleaned);
    }
  };

  const handleUnitBlur = (index, value) => {
    const standardized = getStandardizedUnit(value);
    if (standardized && standardized !== value) {
      handleIngredientChange(index, "measurement_name", standardized);
    }
  };

  const handleAmountBlur = (index, value) => {
    const parsed = parseFraction(value);
    if (parsed !== value) {
      handleIngredientChange(index, "amount", String(parsed));
    }
  };

  const addIngredientField = () => {
    setIsDirty(true);
    setRecipeData({
      ...recipeData,
      ingredients: [...recipeData.ingredients, { ingredient_name: "", amount: "", measurement_name: "" }]
    });
  };

  const deleteIngredient = (index) => {
    setIsDirty(true);
    const updatedIngredients = recipeData.ingredients.filter((_, i) => i !== index);
    setRecipeData({
      ...recipeData,
      ingredients: updatedIngredients.length > 0 ? updatedIngredients : [{ ingredient_name: "", amount: "", measurement_name: "" }]
    });
  };

  // Quick Paste Handler
  const handleQuickPasteSubmit = () => {
    if (!quickPasteText.trim()) return;
    const parsed = parseBulkIngredients(quickPasteText);
    if (parsed.length === 0) return;

    setIsDirty(true);
    if (quickPasteMode === "replace") {
      setRecipeData({ ...recipeData, ingredients: parsed });
    } else {
      const existing = recipeData.ingredients.filter(i => i.ingredient_name.trim() !== "");
      setRecipeData({ ...recipeData, ingredients: [...existing, ...parsed] });
    }

    setQuickPasteText("");
    setQuickPasteOpen(false);
  };

  // Steps Handlers
  const handleStepChange = (index, value) => {
    setIsDirty(true);
    const updatedSteps = [...recipeData.steps];
    updatedSteps[index].instruction = value;
    setRecipeData({ ...recipeData, steps: updatedSteps });
  };

  const addStepField = () => {
    setIsDirty(true);
    setRecipeData({
      ...recipeData,
      steps: [...recipeData.steps, { id: Date.now(), step_number: recipeData.steps.length + 1, instruction: "" }]
    });
  };

  const deleteStep = (index) => {
    setIsDirty(true);
    const updatedSteps = recipeData.steps
      .filter((_, i) => i !== index)
      .map((step, i) => ({ ...step, step_number: i + 1 }));
    setRecipeData({
      ...recipeData,
      steps: updatedSteps.length > 0 ? updatedSteps : [{ id: Date.now(), step_number: 1, instruction: "" }]
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setIsDirty(true);
      setRecipeData((prevData) => {
        const oldIndex = prevData.steps.findIndex((step) => step.id === active.id);
        const newIndex = prevData.steps.findIndex((step) => step.id === over.id);
        const reorderedSteps = arrayMove(prevData.steps, oldIndex, newIndex).map((step, index) => ({
          ...step,
          step_number: index + 1
        }));
        return { ...prevData, steps: reorderedSteps };
      });
    }
  };

  // --- 5. Photo Handling (Direct Upload & URL) ---
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const user = auth.currentUser;
    if (!user) {
      setError("Please log in to upload photos.");
      return;
    }

    setIsUploadingImage(true);
    setError(null);

    try {
      const cleanFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
      const storageRef = ref(storage, `recipe_images/${user.uid}/${Date.now()}_${cleanFileName}`);
      const snap = await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(snap.ref);

      setIsDirty(true);
      setRecipeData(prev => ({
        ...prev,
        images: [...prev.images, downloadUrl]
      }));
    } catch (err) {
      console.error("Photo upload error:", err);
      setError("Failed to upload image. Please check your connection or image size.");
    } finally {
      setIsUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleAddManualUrl = () => {
    if (!manualImageUrl.trim()) return;
    setIsDirty(true);
    setRecipeData(prev => ({
      ...prev,
      images: [...prev.images, manualImageUrl.trim()]
    }));
    setManualImageUrl("");
  };

  const handleDeleteImage = (index) => {
    setIsDirty(true);
    setRecipeData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // --- 6. Submit Handler (Firestore updateDoc & Dictionary Sync) ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    const errors = {};
    if (!recipeData.recipe_name.trim()) errors.recipe_name = "Recipe name is required";
    if (!recipeData.cuisine.trim()) errors.cuisine = "Cuisine is required";
    if (!recipeData.servings) errors.servings = "Required";

    const validIngredients = recipeData.ingredients.filter(i => i.ingredient_name.trim() !== "");
    if (validIngredients.length === 0) {
      errors.ingredients = "At least one ingredient is required";
    }

    const validSteps = recipeData.steps.filter(s => s.instruction.trim() !== "");
    if (validSteps.length === 0) {
      errors.steps = "At least one instruction step is required";
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setError("Please fill out the required recipe fields.");
      window.scrollTo(0, 0);
      return;
    }

    setIsSubmitting(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        setError("You must be logged in to update.");
        setIsSubmitting(false);
        return;
      }

      const standardizedIngredients = validIngredients.map(ing => ({
        ingredient_name: toTitleCase(ing.ingredient_name),
        amount: ing.amount ? String(parseFraction(ing.amount)) : "",
        measurement_name: getStandardizedUnit(ing.measurement_name)
      }));

      const standardizedSteps = validSteps.map((step, idx) => ({
        step_number: idx + 1,
        instruction: step.instruction.trim()
      }));

      const finalCuisine = toTitleCase(recipeData.cuisine);

      const submissionData = {
        recipe_name: toTitleCase(recipeData.recipe_name),
        title: toTitleCase(recipeData.recipe_name),
        description: recipeData.description.trim() || "Delicious family recipe.",
        cuisine: finalCuisine,
        difficulty: recipeData.difficulty || "Medium",
        prep_time: Number(recipeData.prep_time) || 0,
        cook_time: Number(recipeData.cook_time) || 0,
        servings: Number(recipeData.servings) || 4,
        ingredients: standardizedIngredients,
        steps: standardizedSteps,
        images: recipeData.images.filter(img => img && img.trim() !== ""),
        tags: (recipeData.tags || []).map(t => toTitleCase(t)),
        updated_at: serverTimestamp()
      };

      const docRef = doc(db, "recipes", id);
      await updateDoc(docRef, submissionData);

      // Asynchronously update central dictionary with any new ingredients or cuisines
      try {
        const dictRef = doc(db, "metadata", "dictionary");
        const dictSnap = await getDoc(dictRef);
        if (dictSnap.exists()) {
          const dictData = dictSnap.data();
          const currentCuisines = new Set(dictData.cuisines || []);
          const currentIngredients = new Set(dictData.ingredients || []);
          let hasUpdates = false;

          if (finalCuisine && !currentCuisines.has(finalCuisine)) {
            currentCuisines.add(finalCuisine);
            hasUpdates = true;
          }

          standardizedIngredients.forEach(i => {
            if (i.ingredient_name && !currentIngredients.has(i.ingredient_name)) {
              currentIngredients.add(i.ingredient_name);
              hasUpdates = true;
            }
          });

          if (hasUpdates) {
            await updateDoc(dictRef, {
              cuisines: Array.from(currentCuisines).sort(),
              ingredients: Array.from(currentIngredients).sort()
            });
          }
        }
      } catch (dictErr) {
        console.log("Dictionary update note:", dictErr);
      }

      setIsDirty(false); 
      navigate(`/recipes/${id}`);
      
    } catch (err) {
      console.error(err);
      setError("Failed to update recipe. Please check all fields.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !recipeData) {
    return (
      <Container maxWidth="sm">
        <Alert severity="error" sx={{ mt: 4 }}>{error}</Alert>
        <Button onClick={() => navigate('/recipes')} sx={{ mt: 2 }}>Back to Recipes</Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mb: 8, mt: 3 }}>
      <Paper elevation={3} sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 3 }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          align="center"
          sx={{ fontFamily: "'Patrick Hand', cursive", color: '#477491' }}
        >
          Edit Recipe
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        
        <Box component="form" onSubmit={handleSubmit} noValidate>
          
          {/* 1. BASIC INFORMATION */}
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#333' }}>
            Basic Information
          </Typography>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                name="recipe_name"
                label="Recipe Title"
                value={recipeData.recipe_name}
                onChange={handleChange}
                fullWidth
                required
                error={!!validationErrors.recipe_name}
                helperText={validationErrors.recipe_name}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                freeSolo
                options={cuisinesList}
                value={recipeData.cuisine}
                onChange={handleCuisineChange}
                onInputChange={(event, newInputValue) => {
                  setIsDirty(true);
                  setRecipeData(prev => ({ ...prev, cuisine: newInputValue }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cuisine Type"
                    required
                    error={!!validationErrors.cuisine}
                    helperText={validationErrors.cuisine || "Select or type a custom cuisine"}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Difficulty</InputLabel>
                <Select
                  name="difficulty"
                  value={recipeData.difficulty}
                  label="Difficulty"
                  onChange={handleChange}
                >
                  <MenuItem value="Easy">Easy (Beginner friendly)</MenuItem>
                  <MenuItem value="Medium">Medium (Average home cooking)</MenuItem>
                  <MenuItem value="Hard">Hard (Advanced techniques)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                name="description"
                label="Story & Description"
                value={recipeData.description}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
              />
            </Grid>
          </Grid>

          {/* 2. TIME & SERVINGS */}
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#333', mt: 2 }}>
            Time & Servings
          </Typography>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                type="number"
                name="prep_time"
                label="Prep Time (minutes)"
                value={recipeData.prep_time}
                onChange={handleChange}
                fullWidth
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                type="number"
                name="cook_time"
                label="Cook Time (minutes)"
                value={recipeData.cook_time}
                onChange={handleChange}
                fullWidth
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                type="number"
                name="servings"
                label="Servings"
                value={recipeData.servings}
                onChange={handleChange}
                fullWidth
                required
                error={!!validationErrors.servings}
                inputProps={{ min: 1 }}
              />
            </Grid>
          </Grid>
          
          {/* 3. TAGS */}
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#333', mt: 2 }}>
            Recipe Tags
          </Typography>
          <Box sx={{ mb: 3.5 }}>
            <Autocomplete
              multiple
              freeSolo
              options={existingTags}
              value={recipeData.tags}
              onChange={handleTagsChange}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Tags"
                  placeholder="e.g. Dinner, Quick, Holiday, Comfort Food"
                  helperText="Press Enter to add custom tags"
                />
              )}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* 4. INGREDIENTS SECTION */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#333' }}>
                Ingredients
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Auto-complete suggests standard pantry items as you type.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<ContentPasteIcon />}
              onClick={() => setQuickPasteOpen(true)}
              sx={{ fontWeight: 'bold' }}
            >
              Quick-Paste List
            </Button>
          </Box>

          {/* Duplicate warning alert */}
          {duplicateIngredients.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Possible Duplicate:</strong> You have added <em>{duplicateIngredients.join(", ")}</em> more than once. You may want to combine amounts.
            </Alert>
          )}

          {validationErrors.ingredients && (
            <Alert severity="error" sx={{ mb: 2 }}>{validationErrors.ingredients}</Alert>
          )}

          {recipeData.ingredients.map((ing, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              {/* Amount */}
              <TextField
                label="Amount"
                value={ing.amount}
                onChange={(e) => handleIngredientChange(index, "amount", e.target.value)}
                onBlur={(e) => handleAmountBlur(index, e.target.value)}
                placeholder="1 1/2"
                size="small"
                sx={{ width: { xs: '25%', sm: '18%' } }}
              />
              
              {/* Unit Autocomplete */}
              <Autocomplete
                freeSolo
                options={STANDARD_MEASUREMENTS}
                value={ing.measurement_name}
                onChange={(event, newValue) => {
                  handleIngredientChange(index, "measurement_name", newValue || "");
                }}
                onInputChange={(event, newInputValue) => {
                  handleIngredientChange(index, "measurement_name", newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Unit"
                    size="small"
                    placeholder="cup"
                    onBlur={(e) => handleUnitBlur(index, e.target.value)}
                  />
                )}
                sx={{ width: { xs: '30%', sm: '22%' } }}
              />
              
              {/* Ingredient Name Autocomplete */}
              <Autocomplete
                freeSolo
                options={ingredientsList}
                value={ing.ingredient_name}
                onChange={(event, newValue) => {
                  handleIngredientChange(index, "ingredient_name", newValue || "");
                }}
                onInputChange={(event, newInputValue) => {
                  handleIngredientChange(index, "ingredient_name", newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ingredient Name"
                    size="small"
                    required
                    fullWidth
                    placeholder="e.g. Olive Oil"
                    onBlur={(e) => handleIngredientNameBlur(index, e.target.value)}
                  />
                )}
                sx={{ flexGrow: 1 }}
              />

              <IconButton onClick={() => deleteIngredient(index)} color="error" size="small">
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}

          <Button startIcon={<AddIcon />} onClick={addIngredientField} variant="text" sx={{ mt: 0.5, mb: 3 }}>
            Add Another Ingredient
          </Button>

          <Divider sx={{ my: 3 }} />

          {/* 5. INSTRUCTIONS SECTION */}
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#333' }}>
            Instructions
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
            Drag and drop using the handle to reorder cooking steps.
          </Typography>

          {validationErrors.steps && (
            <Alert severity="error" sx={{ mb: 2 }}>{validationErrors.steps}</Alert>
          )}

          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={recipeData.steps.map(s => s.id)} strategy={verticalListSortingStrategy}>
              {recipeData.steps.map((step, index) => (
                <SortableStep
                  key={step.id}
                  step={step}
                  index={index}
                  handleStepChange={handleStepChange}
                  deleteStep={deleteStep}
                  error={validationErrors[`steps[${index}]`]}
                />
              ))}
            </SortableContext>
          </DndContext>

          <Button startIcon={<AddIcon />} onClick={addStepField} variant="text" sx={{ mt: 0.5, mb: 3 }}>
            Add Next Step
          </Button>

          <Divider sx={{ my: 3 }} />

          {/* 6. PHOTOS SECTION */}
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#333' }}>
            Recipe Photos
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
            Upload directly from your phone or computer, or paste a photo link.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
            {/* Direct Upload Button */}
            <input
              type="file"
              accept="image/*"
              hidden
              ref={fileInputRef}
              onChange={handleFileUpload}
            />
            <Button
              variant="outlined"
              color="primary"
              startIcon={isUploadingImage ? <CircularProgress size={18} /> : <AddPhotoAlternateIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploadingImage}
              sx={{ fontWeight: 'bold' }}
            >
              {isUploadingImage ? "Uploading..." : "Upload Photo"}
            </Button>

            {/* URL Fallback Input */}
            <Box sx={{ display: 'flex', gap: 1, flexGrow: 1, minWidth: 260 }}>
              <TextField
                size="small"
                fullWidth
                label="Or paste image URL"
                value={manualImageUrl}
                onChange={(e) => setManualImageUrl(e.target.value)}
                placeholder="https://..."
              />
              <Button variant="contained" onClick={handleAddManualUrl} disabled={!manualImageUrl.trim()}>
                Add URL
              </Button>
            </Box>
          </Box>

          {/* Photos Preview Gallery */}
          {recipeData.images.length > 0 && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
              {recipeData.images.map((imgUrl, index) => (
                <Box key={index} sx={{ position: 'relative', width: 110, height: 110, borderRadius: 2, overflow: 'hidden', border: '1px solid #ddd' }}>
                  <Box
                    component="img"
                    src={imgUrl}
                    alt={`Recipe photo ${index + 1}`}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => { e.currentTarget.src = "/no-image.png"; }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteImage(index)}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      bgcolor: 'rgba(0,0,0,0.6)',
                      color: '#fff',
                      '&:hover': { bgcolor: 'rgba(211,47,47,0.9)' },
                      p: 0.5
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {/* 7. ACTION BUTTONS */}
          <Box sx={{ mt: 5, textAlign: 'center', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'center' }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={isSubmitting}
              sx={{ minWidth: 200, width: { xs: '100%', sm: 'auto' }, py: 1.5, fontWeight: 'bold', fontSize: '1.05rem', backgroundColor: '#477491' }}
            >
              {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Update Recipe"}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate(`/recipes/${id}`)}
              sx={{ minWidth: 160, width: { xs: '100%', sm: 'auto' }, py: 1.5 }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* QUICK-PASTE INGREDIENTS DIALOG */}
      <Dialog open={quickPasteOpen} onClose={() => setQuickPasteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Quick-Paste Ingredients</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Paste your ingredients list (one per line). The smart parser will automatically extract amounts, units, and standardize ingredient names!
          </DialogContentText>
          <TextField
            autoFocus
            multiline
            rows={8}
            fullWidth
            placeholder={"1 1/2 cups all-purpose flour\n2 tbsp sugar\n1 tsp vanilla extract\n1/2 cup softened butter\n2 eggs\n1/4 tsp salt"}
            value={quickPasteText}
            onChange={(e) => setQuickPasteText(e.target.value)}
            variant="outlined"
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Action</InputLabel>
            <Select
              value={quickPasteMode}
              label="Action"
              onChange={(e) => setQuickPasteMode(e.target.value)}
            >
              <MenuItem value="replace">Replace all current ingredients</MenuItem>
              <MenuItem value="append">Add to existing ingredients</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setQuickPasteOpen(false)}>Cancel</Button>
          <Button
            onClick={handleQuickPasteSubmit}
            variant="contained"
            color="primary"
            disabled={!quickPasteText.trim()}
          >
            Parse & Insert Ingredients
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EditRecipe;